import type { InputHTMLAttributes, ReactNode } from 'react'
import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'
import ErrorIcon from '../../../components/icons/ErrorIcon'
import { textStyle } from '../../helpers'
import { Typography } from '../Typography'

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string | boolean
  helperText?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  fullWidth?: boolean
  // extraMessage?: JSX.IntrinsicElements['span']
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      error,
      helperText,
      startAdornment,
      endAdornment,
      fullWidth = false,
      required,
      id,
      // extraMessage,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const hasError = !!error
    const errorMessage = typeof error === 'string' ? error : ''
    const displayMessage = hasError ? errorMessage : helperText

    return (
      <Container $fullWidth={fullWidth}>
        {label && (
          <Label htmlFor={inputId}>
            <Typography variant="Heading/Heading-14-Semibold" color="label">
              {label}
              {required && <RequiredMark>*</RequiredMark>}
            </Typography>
          </Label>
        )}
        <InputWrapper $hasError={hasError} $disabled={!!props.disabled}>
          {startAdornment && <Adornment>{startAdornment}</Adornment>}
          <StyledInput
            ref={ref}
            id={inputId}
            aria-invalid={hasError}
            aria-describedby={displayMessage ? `${inputId}-helper` : undefined}
            required={required}
            {...props}
          />
          {endAdornment && <Adornment>{endAdornment}</Adornment>}
        </InputWrapper>
        {displayMessage && (
          <HelperTextWrapper id={`${inputId}-helper`}>
            {hasError && <ErrorIcon />}
            <Typography
              variant="Heading/Heading-14-medium"
              color={hasError ? 'danger' : 'textSecondary'}
            >
              {displayMessage}
            </Typography>
          </HelperTextWrapper>
        )}
        {/* {extraMessage && <>{extraMessage}</>} */}
      </Container>
    )
  }
)

TextInput.displayName = 'TextInput'

const Container = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  gap: 0.5rem;
`

const Label = styled.label`
  display: block;
`

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  /* margin-left: 4px; */
`

const InputWrapper = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  position: relative;
  padding: 1.125rem 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  /* gap: 8px; */
  /* height: 44px; */
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border};
  border-radius: 0.5rem;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.danger};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: ${({ theme }) => theme.palette.gray[50]};
      cursor: not-allowed;
    `}

  &:hover:not(:focus-within) {
    ${({ $hasError, $disabled, theme }) =>
      !$disabled &&
      !$hasError &&
      css`
        border-color: ${theme.colors.textSecondary};
      `}
  }
`

const StyledInput = styled.input`
  display: flex;
  width: 100%;
  padding: 0;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.text};
  ${() => textStyle('Button/Button-16-medium')}

  &::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

const Adornment = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
`

const HelperTextWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
`

// const MessagePlaceholder = styled.div`
//   height: 20px;
// `
